// Platformgegevens (ADR 0007): alleen geaggregeerde cijfers uit /v1/platform/*. Geen persoonsgegevens.
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.ts";

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  plan: string;
  isDemo: boolean;
  country: string;
  baseLocale: string;
  currency: string;
  createdAt: string;
  members: number;
  sites: number;
  properties: { total: number; ready: number; draft: number; available: number };
  leads: { d30: number; d90: number; spam30: number; won90: number; lost90: number; open: number; new: number; medianResponseS: number | null };
  ai: { monthMinor: number; tokensMonth: number; budgetMinor: number | null; hardStop: boolean | null; jobs30: number; blocked30: number };
}

export interface PlatformSite {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  baseLocale: string;
  host: string | null;
  locales: { tag: string; mode: "published" | "preview" | "off" }[];
  live: { revision: number | null; generation: number | null; promotedAt: string | null } | null;
  lastBuild: { status: string; createdAt: string; ms: number | null; strategy: string | null; pages: number | null } | null;
  builds30: { count: number; failed: number; stuck: number; p50Ms: number | null; p90Ms: number | null };
}

export interface QualityPoint {
  siteId: string;
  formFactor: "mobile" | "desktop";
  status: string;
  measuredAt: string;
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
  metrics: QualityMetrics;
}

export interface QualityMetrics {
  lcpMs?: number;
  fcpMs?: number;
  tbtMs?: number;
  siMs?: number;
  cls?: number;
  ttfbMs?: number;
  issues?: { id: string; category: string; title: string; score: number }[];
}

export interface RegistryInfo {
  provider: "google_analytics_4" | "google_search_console";
  name: string;
  evidenceLevel: string;
  missing: string[];
  docs: { label: string; url: string }[];
  verifiedOn: string;
}

/** Laatste meetjob (site.quality) per site. */
export interface QualityJob {
  siteId: string;
  jobId: string;
  status: "queued" | "running" | "succeeded" | "failed" | "dead" | "cancelled";
  attempts: number;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  lastError: string | null;
  result: { url: string; runs: { formFactor: "mobile" | "desktop"; ok: boolean; performance: number | null; seo: number | null; error: string | null }[] } | null;
}

export const jobActive = (job: QualityJob | null | undefined) => job?.status === "queued" || job?.status === "running";

export interface PlatformOverview {
  generatedAt: string;
  tenants: PlatformTenant[];
  sites: PlatformSite[];
  builds: { siteId: string; channel: string; status: string; strategy: string | null; createdAt: string; ms: number | null; pages: number | null; rendered: number | null }[];
  leadsDaily: { day: string; tenantId: string; channel: "website" | "portal" | "other"; count: number }[];
  funnel: { tenantId: string; status: string; count: number }[];
  locales: { tenantId: string; siteId: string; tag: string; mode: string; units: number; approved: number; stale: number; inReview: number }[];
  aiMonthly: { month: string; tenantId: string; costMinor: number; tokens: number | null }[];
  channels: { tenantId: string; provider: string; name: string; kind: string; evidence: string; direction: string; status: string; killSwitch: boolean; listings: Record<string, number> }[];
  quality: QualityPoint[];
  analytics: { siteId: string; provider: string; configured: boolean; lastSyncAt: string | null; hasError: boolean }[];
  connectors: { registry: RegistryInfo[]; credentials: "missing" | "invalid" | "present" };
  qualityJobs: QualityJob[];
}

export interface QualityRun {
  id: string;
  url: string;
  formFactor: "mobile" | "desktop";
  toolVersion: string;
  status: "succeeded" | "failed";
  measuredAt: string;
  performance: number | null;
  accessibility: number | null;
  bestPractices: number | null;
  seo: number | null;
  metrics: QualityMetrics;
  error: string | null;
  durationMs: number | null;
}

export type LinkStatus = "not_configured" | "awaiting_credentials" | "awaiting_first_sync" | "connected" | "error";

export interface PlatformSiteDetail {
  site: {
    id: string;
    tenantId: string;
    tenantName: string;
    tenantSlug: string;
    key: string;
    name: string;
    baseLocale: string;
    hosts: { host: string; kind: string; verified: boolean }[];
    locales: { tag: string; mode: string }[];
    live: { revision: number | null; promotedAt: string | null } | null;
  };
  runs: QualityRun[];
  analytics: { provider: RegistryInfo["provider"]; propertyRef: string | null; lastSyncAt: string | null; lastError: string | null; status: LinkStatus }[];
  traffic: { provider: string; day: string; metrics: Record<string, number> }[];
  connectors: { credentials: { state: "missing" | "invalid" | "present"; clientEmail?: string; reason?: string }; registry: RegistryInfo[] };
  qualityJob: QualityJob | null;
}

// Loopt er een meting, dan elke 3 s verversen tot hij klaar is; anders rustig.
export function usePlatformOverview(enabled = true) {
  return useQuery({
    queryKey: ["platform", "overview"],
    enabled,
    queryFn: () => api.get<PlatformOverview>("/v1/platform/overview"),
    staleTime: 60_000,
    refetchInterval: (q) => (q.state.data?.qualityJobs.some(jobActive) ? 3000 : 120_000),
  });
}

export function usePlatformSite(siteId: string) {
  return useQuery({
    queryKey: ["platform", "site", siteId],
    queryFn: () => api.get<PlatformSiteDetail>(`/v1/platform/sites/${siteId}`),
    staleTime: 60_000,
    refetchInterval: (q) => (jobActive(q.state.data?.qualityJob) ? 3000 : false),
  });
}

export const QUALITY_KEYS = ["performance", "accessibility", "bestPractices", "seo"] as const;
export type QualityKey = (typeof QUALITY_KEYS)[number];

/** Laatste geslaagde meting per site en vormfactor. */
export function latestQuality(points: QualityPoint[], siteId: string, form: "mobile" | "desktop"): QualityPoint | undefined {
  return points.filter((p) => p.siteId === siteId && p.formFactor === form).at(-1);
}

/** Mediaan (ms) van een reeks; null zonder waarden. */
export function median(values: number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** Maandag van de week van een datum (UTC), als YYYY-MM-DD. */
export function weekStart(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

/** De laatste n weken (maandagen), oudste eerst, tot en met deze week. */
export function lastWeeks(n: number, now = new Date()): string[] {
  const current = weekStart(now.toISOString().slice(0, 10));
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(`${current}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - (n - 1 - i) * 7);
    return d.toISOString().slice(0, 10);
  });
}

/** De laatste n kalenderdagen (UTC), oudste eerst. */
export function lastDays(n: number, now = new Date()): string[] {
  return Array.from({ length: n }, (_, i) => new Date(now.getTime() - (n - 1 - i) * 86_400_000).toISOString().slice(0, 10));
}
